<template lang="pug">
.header
  .header__loader(v-if="loading")
  .header__data(v-else)
    .header__data-item(v-for="value in headerData", :key="value.name", :class="value.name")
      .header__data-item__title
        .header__data-item__title-icon
          inline-svg(:src="require(`../../../../../assets/icons/cleaning_dashboard/${value.name}.svg`)")
        .header__data-item__title-label {{ value.title }}
      .header__data-item__values
        template(v-if="value.name === 'worked_hours'")
          .header__data-item__values-item {{ value.data.value }}
        template(v-else)
          .header__data-item__values-item(
            v-for="(dataValue, dataKey) in value.data",
            :key="`${value.name}_${dataKey}`",
            :class="dataKey"
          )
            .header__data-item__values-item__value {{ dataValue.value }}
            .header__data-item__values-item__label {{ dataValue.title }}
</template>

<script setup>
import { ref, computed, onMounted } from "vue";
import backend from "@/api";
import { handleError } from "@/services/handleErrors";
import { parse } from "date-fns";

const props = defineProps({
  /* id текущей основной орагнизации выбранной пользователем для дашборда в фильтрах */
  facilityId: { type: Number, default: -1 },
  /** Тип дат за которые запрашиваются данные. Пример: "today" */
  dateType: { type: String, default: "today" },
  /** Фильтр даты от. Пример: "2025-10-28T21:00:00.000Z" */
  dateFrom: { type: String, default: "" },
  /** Фильтр даты до. Пример: "2025-10-29T21:00:00.000Z" */
  dateTo: { type: String, default: "" },
  /** Дополнительные фильтры. Пример: { building_ids: [1, 2, 3] } */
  additionalFilters: { type: Object, default: () => {} },
  /** Фильтр даты от при произвольном промежутке. Пример: "01.01.1970" */
  customDateFrom: { type: String, default: () => "" },
  /** Фильтр даты до при произвольном промежутке. Пример: "01.01.1970" */
  customDateTo: { type: String, default: () => "" },
});

const path = "/api/v3/cleaning/dashboard";

const dateType = computed(() => props.dateType);
const itsOneDay = computed(() => {
  const startedAt = parse(props.customDateFrom, "dd.MM.yyyy", new Date());
  const finishedAt = parse(props.customDateTo, "dd.MM.yyyy", new Date());
  startedAt.setHours(0, 0, 0, 0);
  finishedAt.setHours(0, 0, 0, 0);
  return (
    (dateType.value === "custom" && Number(startedAt) === Number(finishedAt)) ||
    dateType.value === "today" ||
    dateType.value === "yesterday"
  );
});
const customDateFrom = computed(() => {
  if (props.customDateFrom === "") {
    return "";
  }
  const dateFrom = parse(props.customDateFrom, "dd.MM.yyyy", new Date());
  return dateFrom.toISOString();
});
const customDateTo = computed(() => {
  if (props.customDateTo === "") {
    return "";
  }
  const dateTo = parse(props.customDateTo, "dd.MM.yyyy", new Date());
  if (itsOneDay.value) {
    dateTo.setHours(24, 0, 0, 0);
  } else {
    dateTo.setHours(23, 59, 59, 999);
  }
  return dateTo.toISOString();
});
const dateFrom = computed(() => (props.dateType === "custom" ? customDateFrom.value : props.dateFrom));
const dateTo = computed(() => (props.dateType === "custom" ? customDateTo.value : props.dateTo));
const additionalFilters = computed(() => props.additionalFilters);

const loading = ref(false);
const headerData = ref([]);

const getData = async () => {
  if (!loading.value) {
    try {
      loading.value = true;

      const defaultParams = {
        facility_id: props.facilityId,
        date_from: dateFrom.value,
        date_to: dateTo.value,
      };

      const params = { ...defaultParams, ...additionalFilters.value };

      const { data } = await backend.index(`${path}/header`, { params }, { encodeNestedData: true });

      headerData.value = data.data;
    } catch (err) {
      await handleError(err, { notify: false });
    } finally {
      loading.value = false;
    }
  }
};

onMounted(() => {
  getData();
});

defineExpose({ getData });
</script>

<style lang="scss" scoped>
.header {
  padding-top: 0 !important;

  .header__data {
    display: flex;
    justify-content: space-between;
    gap: 12px;

    &-item {
      border: 1px solid var(--cleaning-dashboard-block-border);
      background-color: var(--cleaning-dashboard-block-background);
      border-radius: 20px;
      padding-right: 20px;
      align-items: center;
      width: 100%;
      height: 72px;
      display: flex;
      justify-content: space-between;
      color: var(--cleaning-dashboard-text-color);

      &__title {
        display: flex;
        justify-content: space-between;
        gap: 12px;
        height: 72px;
        align-items: center;
        overflow: hidden;

        &-icon {
          min-width: 68px;
          width: 68px;
          height: calc(100% - 2px);
          border-radius: 20px;
          display: flex;
          justify-content: center;
          align-items: center;
          background-color: #ccc;
        }

        &-label {
          font-weight: 400;
          font-size: 14px;
        }
      }

      &.worked_hours .header__data-item__title-icon {
        background-color: var(--cleaning-dashboard-header-item-icon-background-worked_hours);
      }

      &.employees .header__data-item__title-icon {
        background-color: var(--cleaning-dashboard-header-item-icon-background-employee);
      }

      &.buildings .header__data-item__title-icon {
        background-color: var(--cleaning-dashboard-header-item-icon-background-buildings);
      }

      &__values {
        display: flex;
        gap: 24px;
        align-items: center;

        &-item {
          position: relative;
          text-align: center;
          font-weight: 600;

          &.active .header__data-item__values-item__value {
            color: #14c783;
          }

          &.inactive .header__data-item__values-item__value {
            color: #fa7167;
          }

          &__label {
            font-size: 12px;
            font-weight: 400;
            color: #78839c;
          }

          &:not(:last-child)::after {
            content: "";
            width: 1px;
            height: 100%;
            position: absolute;
            right: -12px;
            top: 0;
            background-color: #aeb5c4;
          }
        }
      }
    }
  }
}
</style>
